<template>
  <div class="bg-white rounded-lg shadow-md p-6">
    <h2 class="text-xl font-semibold mb-4">Email Template Processing Test</h2>
    
    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center items-center py-6">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      <span class="ml-2">Loading template...</span>
    </div>
    
    <!-- Error state -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-6">
      <strong class="font-bold">Error:</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>
    
    <div v-else>
      <!-- Template Selection -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">Select Template</label>
        <select 
          v-model="selectedTemplateId"
          @change="loadTemplate"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
        >
          <option value="">-- Select a template --</option>
          <option v-for="template in availableTemplates" :key="template.id" :value="template.id">
            {{ template.template_name }}
          </option>
        </select>
      </div>
      
      <!-- Variable Values -->
      <div v-if="originalTemplate" class="mb-6">
        <h3 class="text-lg font-medium mb-2">Variable Values</h3>
        <p class="text-sm text-gray-500 mb-3">
          Enter values for the variables in this template. These will be used to replace the placeholders.
        </p>
        
        <div class="space-y-3">
          <div v-for="(value, key) in variableValues" :key="key" class="grid grid-cols-3 gap-4">
            <div class="col-span-1">
              <label class="block text-sm font-medium text-gray-700">{{ key }}</label>
            </div>
            <div class="col-span-2">
              <input 
                type="text" 
                v-model="variableValues[key]"
                @input="processTemplate"
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                :placeholder="`Value for ${key}`"
              />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Original Template -->
      <div v-if="originalTemplate" class="mb-6">
        <h3 class="text-lg font-medium mb-2">Original Template</h3>
        <div class="border border-gray-200 rounded-md p-4 bg-gray-50">
          <div class="mb-2">
            <span class="font-medium">Subject:</span> {{ originalTemplate.subject }}
          </div>
          <div>
            <span class="font-medium">Body:</span>
            <div class="mt-2 whitespace-pre-wrap">{{ originalTemplate.content }}</div>
          </div>
        </div>
      </div>
      
      <!-- Processed Template -->
      <div v-if="processedTemplate" class="mb-6">
        <h3 class="text-lg font-medium mb-2">Processed Template</h3>
        <div class="border border-gray-200 rounded-md p-4 bg-white">
          <div class="mb-2">
            <span class="font-medium">Subject:</span> {{ processedTemplate.subject }}
          </div>
          <div>
            <span class="font-medium">Body:</span>
            <div class="mt-2 whitespace-pre-wrap">{{ processedTemplate.content }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, reactive } from 'vue';
import { emailTemplateService, EmailTemplate, TemplateVariables } from '@/services/emailTemplate';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';

export default defineComponent({
  name: 'EmailTemplateTest',
  setup() {
    const isLoading = ref(false);
    const error = ref('');
    const availableTemplates = ref<EmailTemplate[]>([]);
    const selectedTemplateId = ref('');
    const originalTemplate = ref<EmailTemplate | null>(null);
    const processedTemplate = ref<EmailTemplate | null>(null);
    const variableValues = reactive<TemplateVariables>({});
    
    // Load available templates
    const loadAvailableTemplates = async () => {
      isLoading.value = true;
      error.value = '';
      
      try {
        const { data, error: fetchError } = await supabase
          .from('email_templates')
          .select('id, category, template_name, subject, content, created_at, updated_at, created_by, is_system');
        
        if (fetchError) {
          throw new Error(`Error fetching templates: ${fetchError.message}`);
        }
        
        // Map the database columns to our EmailTemplate interface
        availableTemplates.value = (data || []).map(item => ({
          id: item.id,
          category: item.category,
          template_name: item.template_name,
          subject: item.subject,
          content: item.content,
          created_at: item.created_at,
          updated_at: item.updated_at,
          created_by: item.created_by,
          is_system: item.is_system
        })) as EmailTemplate[];
      } catch (err: any) {
        error.value = err.message;
        logger.error('Error loading templates', LogCategory.DATABASE, { error: err });
      } finally {
        isLoading.value = false;
      }
    };
    
    // Load a specific template
    const loadTemplate = async () => {
      if (!selectedTemplateId.value) {
        originalTemplate.value = null;
        processedTemplate.value = null;
        return;
      }
      
      isLoading.value = true;
      error.value = '';
      
      try {
        const template = await emailTemplateService.getTemplateById(selectedTemplateId.value);
        
        if (!template) {
          throw new Error(`Template with ID ${selectedTemplateId.value} not found`);
        }
        
        originalTemplate.value = template;
        
        // Extract variables from the template
        extractVariables(template);
        
        // Process the template with empty values initially
        processTemplate();
      } catch (err: any) {
        error.value = err.message;
        logger.error('Error loading template', LogCategory.DATABASE, { error: err });
      } finally {
        isLoading.value = false;
      }
    };
    
    // Extract variables from the template
    const extractVariables = (template: EmailTemplate) => {
      // Reset variable values
      Object.keys(variableValues).forEach(key => {
        delete variableValues[key];
      });
      
      // Extract variables from subject and content
      const variableRegex = /\{\{([^}]+)\}\}/g;
      const text = template.subject + ' ' + template.content;
      let match;
      
      while ((match = variableRegex.exec(text)) !== null) {
        const variableName = match[1].trim();
        if (!Object.prototype.hasOwnProperty.call(variableValues, variableName)) {
          variableValues[variableName] = '';
        }
      }
    };
    
    // Process the template with current variable values
    const processTemplate = () => {
      if (!originalTemplate.value) return;
      
      processedTemplate.value = emailTemplateService.processTemplate(
        originalTemplate.value,
        variableValues
      );
      
      logger.info('Template processed with variables', LogCategory.GENERAL, {
        variables: Object.keys(variableValues),
        template: originalTemplate.value.template_name
      });
    };
    
    // Load templates on component mount
    onMounted(() => {
      loadAvailableTemplates();
    });
    
    return {
      isLoading,
      error,
      availableTemplates,
      selectedTemplateId,
      originalTemplate,
      processedTemplate,
      variableValues,
      loadTemplate,
      processTemplate
    };
  }
});
</script>